import { useEffect, useState } from "react";
import { Sparkles, Play, Loader2, TrendingUp, RotateCcw, Check, Pencil, MessageSquare, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Send, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import api, { formatApiError } from "../../lib/api";

const WD = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const DEFAULT_LADDER = [100, 70, 60, 50, 40, 30, 30, 30];

export default function AdminPricing() {
  const [cfg, setCfg] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [props, setProps] = useState([]);
  const [propId, setPropId] = useState("");
  const [roomId, setRoomId] = useState("");
  const [assistantMsg, setAssistantMsg] = useState("");
  const [assistantReply, setAssistantReply] = useState("");
  const [assistantBusy, setAssistantBusy] = useState(false);
  const [resetStart, setResetStart] = useState(() => new Date().toISOString().slice(0, 10));
  const [resetEnd, setResetEnd] = useState(() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10); });
  const [resetting, setResetting] = useState(false);

  const selectedProp = props.find((p) => p.id === propId) || null;
  const isHotel = selectedProp?.type === "hotel";

  const maxBeds = Math.max(8, ...props.map((p) => p.type === "hotel"
    ? Math.max(0, ...(p.room_types || []).map((rt) => rt.max_guests || 0))
    : (p.max_guests || 0)));
  const ladder = Array.from({ length: maxBeds }, (_, i) => {
    if (i === 0) return 100;
    const v = cfg?.occupancy_pct?.[i];
    return v != null ? v : (DEFAULT_LADDER[i] != null ? DEFAULT_LADDER[i] : 30);
  });
  const setLadder = (i, val) => {
    if (i === 0) return;
    const arr = ladder.slice();
    arr[i] = val;
    setCfg({ ...cfg, occupancy_pct: arr });
  };

  const loadCfg = () => api.get("/admin/settings").then((r) => setCfg(r.data.pricing));
  const loadProps = () => api.get("/admin/apartments").then((r) => setProps(r.data)).catch(() => {});
  const loadTimeline = (start = startDate, pid = propId, rid = roomId) => {
    const params = new URLSearchParams({ days: "30", start });
    if (pid) params.set("property_id", pid);
    if (rid) params.set("room_type_id", rid);
    return api.get(`/admin/pricing/timeline?${params.toString()}`).then((r) => setTimeline(r.data)).catch(() => {});
  };
  const loadLogs = () => api.get("/admin/pricing/logs").then((r) => setLogs(r.data)).catch(() => {});
  useEffect(() => { loadCfg(); loadProps(); loadLogs(); }, []);
  useEffect(() => { loadTimeline(startDate, propId, roomId); /* eslint-disable-next-line */ }, [propId, roomId]);

  const shiftPeriod = (deltaDays) => {
    const d = new Date(startDate);
    d.setDate(d.getDate() + deltaDays);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (d < today) return;
    const iso = d.toISOString().slice(0, 10);
    setStartDate(iso);
    loadTimeline(iso);
  };

  const askAssistant = async () => {
    if (!assistantMsg.trim()) return;
    setAssistantBusy(true);
    setAssistantReply("");
    try {
      const { data } = await api.post("/admin/pricing/assistant", { message: assistantMsg, apartment_id: propId || null, room_type_id: roomId || null });
      setAssistantReply(data.reply + (data.applied ? ` (${data.applied} date aggiornate)` : ""));
      setAssistantMsg("");
      loadTimeline();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAssistantBusy(false); }
  };

  const save = async () => {
    setSaving(true);
    const occ = ladder.slice(); occ[0] = 100;
    try { await api.put("/admin/settings/pricing", { ...cfg, min_billed_nights: cfg.min_billed_nights ?? 3, child_discount: cfg.child_discount ?? 0.5, occupancy_pct: occ }); toast.success("Configurazione salvata"); loadCfg(); loadTimeline(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/pricing/run");
      if (data.status === "disabled") toast.error("Dynamic pricing disattivato");
      else toast.success(`Analisi completata · ${data.notable_count} aggiustamenti`);
      loadLogs(); loadTimeline();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const setOverride = async (dateStr, price) => {
    try {
      await api.post("/admin/pricing/override", { date: dateStr, price, apartment_id: propId || null, room_type_id: roomId || null });
      toast.success(price ? "Prezzo aggiornato" : "Ripristinato prezzo IA");
      loadTimeline();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resetRange = async () => {
    if (!resetStart || !resetEnd) return toast.error("Imposta il periodo da ripristinare");
    setResetting(true);
    try {
      const { data } = await api.post("/admin/pricing/reset-range-period", { start: resetStart, end: resetEnd, apartment_id: propId || null, room_type_id: roomId || null });
      toast.success(`${data.removed} prezzi ripristinati ai valori del dynamic pricing`);
      loadTimeline();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setResetting(false); }
  };

  if (!cfg) return <Loader2 className="animate-spin text-fir" />;

  const chartData = (timeline?.nights || []).map((n) => ({ date: n.date.slice(5), price: n.price }));

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-4xl text-fir flex items-center gap-2"><Sparkles className="text-gold" /> Dynamic Pricing IA</h1>
          <p className="text-fir-deep/60">Prezzi <b>per persona</b> proposti dall'IA (stagionalità, eventi, competitor), modificabili a mano nella timeline.</p>
        </div>
        <button data-testid="run-pricing-btn" onClick={run} disabled={running} className="gold-btn px-6 py-3 rounded-xl flex items-center gap-2">
          {running ? <Loader2 className="animate-spin" size={18} /> : <Play size={18} />} Esegui analisi IA
        </button>
      </div>

      {/* PROPERTY SELECTOR */}
      <div className="bg-white rounded-2xl p-5 border border-stone mb-6 flex flex-col md:flex-row md:items-end gap-4">
        <div className="flex-1">
          <label className="text-xs uppercase tracking-wide text-fir-deep/60">Struttura</label>
          <select data-testid="pricing-property-select" value={propId}
            onChange={(e) => { setPropId(e.target.value); setRoomId(""); }}
            className="input-alpine w-full mt-1">
            <option value="">Prezzi globali (default)</option>
            {props.map((p) => <option key={p.id} value={p.id}>{p.type === "hotel" ? "🏨 " : "🏠 "}{p.title}</option>)}
          </select>
        </div>
        {isHotel && (
          <div className="flex-1">
            <label className="text-xs uppercase tracking-wide text-fir-deep/60">Tipologia camera</label>
            <select data-testid="pricing-room-select" value={roomId}
              onChange={(e) => setRoomId(e.target.value)} className="input-alpine w-full mt-1">
              <option value="">Seleziona una camera…</option>
              {(selectedProp?.room_types || []).map((rt) => <option key={rt.id} value={rt.id}>{rt.name}</option>)}
            </select>
          </div>
        )}
        <p className="text-xs text-fir-deep/50 md:max-w-xs">
          {propId ? "Timeline, override e assistente agiscono su questa struttura." : "Nessuna struttura selezionata: gestisci la configurazione e la timeline globali."}
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-2xl p-6 border border-stone">
          <h2 className="font-display text-2xl text-fir mb-4">Configurazione</h2>
          <label className="flex items-center gap-3 mb-4 cursor-pointer">
            <input type="checkbox" data-testid="pricing-enabled" checked={cfg.enabled}
              onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} className="h-5 w-5 accent-[#D4AF37]" />
            <span className="text-fir">Pricing dinamico attivo</span>
          </label>
          <p className="text-xs text-fir-deep/50 mb-4">Prezzi <b>per persona a notte</b>. Ogni ospite successivo al 1° paga una percentuale del prezzo (vedi tabella "Percentuali per ospite" qui sotto).</p>
          <NumRow label="Prezzo base €/persona/notte" value={cfg.base_price} onChange={(v) => setCfg({ ...cfg, base_price: v })} testid="pricing-base" />
          <NumRow label="Prezzo minimo €/persona" value={cfg.min_price} onChange={(v) => setCfg({ ...cfg, min_price: v })} testid="pricing-min" />
          <NumRow label="Prezzo massimo €/persona" value={cfg.max_price} onChange={(v) => setCfg({ ...cfg, max_price: v })} testid="pricing-max" />
          <NumRow label="Minimo notti fatturate" value={cfg.min_billed_nights ?? 3} onChange={(v) => setCfg({ ...cfg, min_billed_nights: v })} testid="pricing-min-nights" />
          <p className="text-xs text-fir-deep/50 -mt-1 mb-3">Soggiorni 1-2 notti fatturati come {cfg.min_billed_nights ?? 3} notti (pulizie).</p>
          <button data-testid="save-pricing-btn" onClick={save} disabled={saving} className="gold-btn w-full py-2.5 rounded-xl mt-1">Salva</button>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-stone md:col-span-2">
          <h2 className="font-display text-2xl text-fir mb-1 flex items-center gap-2"><TrendingUp size={20} className="text-wood" /> Andamento 30 giorni (per persona)</h2>
          <p className="text-xs text-fir-deep/50 mb-4">
            Media competitor: {timeline?.competitor?.average ? `€${timeline.competitor.average}` : "n/d (scraping best-effort)"}
          </p>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData}>
              <XAxis dataKey="date" tick={{ fontSize: 11 }} interval={4} />
              <YAxis domain={[cfg.min_price - 10, cfg.max_price + 10]} tick={{ fontSize: 11 }} />
              <Tooltip />
              <ReferenceLine y={cfg.min_price} stroke="#C68B59" strokeDasharray="4 4" />
              <ReferenceLine y={cfg.max_price} stroke="#C68B59" strokeDasharray="4 4" />
              <Line type="monotone" dataKey="price" stroke="#1E3F20" strokeWidth={2.5} dot={{ r: 2, fill: "#D4AF37" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* OCCUPANCY LADDER */}
      <div className="bg-white rounded-2xl p-6 border border-stone mb-8">
        <h2 className="font-display text-2xl text-fir mb-1">Percentuali per ospite</h2>
        <p className="text-xs text-fir-deep/50 mb-4">Il <b>1° ospite paga il 100%</b> del prezzo per persona; ogni ospite successivo paga la percentuale indicata. Vale per <b>tutte le strutture</b>. La tabella si estende automaticamente fino ai posti letto massimi ({maxBeds}); le posizioni non impostate valgono 30%.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-4">
          {ladder.map((v, i) => (
            <div key={i}>
              <label className="text-xs uppercase tracking-wide text-fir-deep/60">{i + 1}° ospite</label>
              <div className="flex items-center gap-1 mt-1">
                <input data-testid={`occupancy-pct-${i}`} type="number" min="0" max="100" value={v} disabled={i === 0}
                  onChange={(e) => setLadder(i, Number(e.target.value))}
                  className={`input-alpine w-full ${i === 0 ? "opacity-60" : ""}`} />
                <span className="text-fir-deep/50">%</span>
              </div>
            </div>
          ))}
        </div>
        <button data-testid="save-occupancy-btn" onClick={save} disabled={saving} className="gold-btn px-6 py-2.5 rounded-xl">
          {saving ? "Salvataggio…" : "Salva percentuali"}
        </button>
      </div>

      {/* AI ASSISTANT */}
      <div className="bg-fir rounded-2xl p-6 mb-8 text-ivory">
        <h2 className="font-display text-2xl mb-1 flex items-center gap-2"><Wand2 size={20} className="text-gold" /> Assistente prezzi IA</h2>
        <p className="text-ivory/70 text-sm mb-4">Dai un'istruzione in linguaggio naturale, es: "abbassa i prezzi di luglio del 10%" oppure "metti 60€ a persona dal 20 al 30 dicembre".</p>
        <div className="flex gap-2">
          <input data-testid="pricing-assistant-input" value={assistantMsg} onChange={(e) => setAssistantMsg(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && askAssistant()}
            placeholder="Scrivi un'istruzione sui prezzi…"
            className="flex-1 px-4 py-3 rounded-xl bg-ivory/10 border border-ivory/20 text-ivory placeholder-ivory/50 outline-none focus:border-gold" />
          <button data-testid="pricing-assistant-send" onClick={askAssistant} disabled={assistantBusy}
            className="gold-btn px-6 py-3 rounded-xl flex items-center gap-2">
            {assistantBusy ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />} Applica
          </button>
        </div>
        {assistantReply && <p data-testid="pricing-assistant-reply" className="mt-3 text-sm text-gold bg-ivory/5 rounded-lg px-3 py-2">{assistantReply}</p>}
      </div>

      {/* TIMELINE EDITABLE */}
      <div className="bg-white rounded-2xl p-6 border border-stone mb-8">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="font-display text-2xl text-fir">Timeline prezzi (modificabile)</h2>
          <div className="flex items-center gap-3">
            <button data-testid="timeline-prev" onClick={() => shiftPeriod(-30)} className="p-2 rounded-lg border border-stone text-fir hover:bg-stone/50"><ChevronLeft size={18} /></button>
            <span data-testid="timeline-period" className="text-sm text-fir-deep/70 min-w-[150px] text-center">
              {timeline ? `${new Date(timeline.start).toLocaleDateString("it-IT", { day: "numeric", month: "short" })} → +30 gg` : "…"}
            </span>
            <button data-testid="timeline-next" onClick={() => shiftPeriod(30)} className="p-2 rounded-lg border border-stone text-fir hover:bg-stone/50"><ChevronRight size={18} /></button>
            <span className="text-sm text-fir-deep/50 ml-2">Fino a {timeline?.max_guests || 6} posti letto</span>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3 mb-4 p-3 rounded-xl bg-ivory/60 border border-stone">
          <div>
            <label className="text-xs uppercase tracking-wide text-fir-deep/60">Ripristina dal</label>
            <input type="date" data-testid="reset-range-start" value={resetStart} onChange={(e) => setResetStart(e.target.value)} className="input-alpine mt-1" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-fir-deep/60">al</label>
            <input type="date" data-testid="reset-range-end" value={resetEnd} min={resetStart} onChange={(e) => setResetEnd(e.target.value)} className="input-alpine mt-1" />
          </div>
          <button data-testid="reset-range-btn" onClick={resetRange} disabled={resetting}
            className="px-4 py-2 rounded-lg border border-fir text-fir hover:bg-fir hover:text-ivory transition-colors flex items-center gap-2 disabled:opacity-50">
            {resetting ? <Loader2 className="animate-spin" size={16} /> : <RotateCcw size={16} />} Ripristina prezzi IA nel periodo
          </button>
          <span className="text-xs text-fir-deep/50 self-center max-w-xs">
            Rimuove le modifiche manuali nel periodo e ripristina i prezzi del dynamic pricing. {propId ? "Agisce sulla struttura selezionata." : "Agisce sui prezzi globali."}
          </span>
        </div>
        <div data-testid="pricing-timeline" className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-fir-deep/60 border-b border-stone">
                <th className="text-left py-2 px-2">Data</th>
                <th className="text-left py-2 px-2">IA €/pers.</th>
                <th className="text-left py-2 px-2">Prezzo effettivo €/pers.</th>
                <th className="text-left py-2 px-2">Suggerimenti per n° persone</th>
                <th className="py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {(timeline?.nights || []).map((n) => (
                <TimelineRow key={n.date} night={n} onCommit={(p) => setOverride(n.date, p)} onReset={() => setOverride(n.date, null)} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-6 border border-stone">
        <button
          data-testid="toggle-ai-logs"
          onClick={() => setShowLogs((v) => !v)}
          className="w-full flex items-center justify-between text-left"
        >
          <h2 className="font-display text-2xl text-fir flex items-center gap-2">
            <MessageSquare size={20} className="text-gold" /> Log AI
            {logs.length > 0 && <span className="text-xs font-sans text-fir-deep/40">({logs.length})</span>}
          </h2>
          {showLogs ? <ChevronUp size={20} className="text-fir-deep/50" /> : <ChevronDown size={20} className="text-fir-deep/50" />}
        </button>
        {showLogs && (
          <div className="mt-4">
            {logs.length === 0 && <p className="text-fir-deep/50 text-sm">Nessuna analisi eseguita. Premi "Esegui analisi IA".</p>}
            <div className="space-y-5">
              {logs.map((run) => (
                <div key={run.id} data-testid="pricing-log-run" className="border-l-2 border-gold pl-4">
                  <p className="text-xs text-fir-deep/50 mb-1">
                    {new Date(run.run_at).toLocaleString("it-IT")} · media €{run.avg_price} · Smoobu: {run.smoobu_status}
                  </p>
                  <ul className="space-y-1">
                    {(run.logs || []).map((l, i) => <li key={i} className="text-sm text-fir-deep/80">• {l}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TimelineRow({ night, onCommit, onReset }) {
  const [val, setVal] = useState(String(night.price));
  const [showEvent, setShowEvent] = useState(false);
  useEffect(() => { setVal(String(night.price)); }, [night.price]);
  const dirty = Number(val) !== Number(night.price);
  const d = new Date(night.date);
  const label = `${WD[night.weekday]} ${d.getDate()}/${d.getMonth() + 1}`;

  return (
    <tr data-testid="timeline-row" className="border-b border-stone/60 hover:bg-ivory/60">
      <td className="py-2 px-2 whitespace-nowrap">
        {label}
        {night.events?.length > 0 && (
          <span className="relative inline-block">
            <button
              type="button"
              data-testid="timeline-event-badge"
              onClick={() => setShowEvent((v) => !v)}
              className="ml-2 text-[10px] text-gold hover:text-fir font-medium cursor-pointer align-middle"
            >
              ● evento{night.events.length > 1 ? ` (${night.events.length})` : ""}
            </button>
            {showEvent && (
              <div
                data-testid="timeline-event-popover"
                className="absolute z-20 left-0 mt-1 w-64 bg-white border border-stone rounded-xl shadow-lg p-3 text-left"
                onMouseLeave={() => setShowEvent(false)}
              >
                <p className="text-[10px] uppercase tracking-wide text-fir-deep/50 mb-1.5">Eventi rilevati</p>
                <ul className="space-y-1.5">
                  {night.events.map((ev, i) => (
                    <li key={i} className="text-xs text-fir-deep flex items-start gap-1.5">
                      <span className="text-gold mt-0.5">●</span> {ev}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </span>
        )}
      </td>
      <td className="py-2 px-2 text-fir-deep/60">€{night.ai_price}</td>
      <td className="py-2 px-2">
        <div className="flex items-center gap-1.5">
          <span className="text-fir-deep/50">€</span>
          <input data-testid="timeline-price-input" type="number" value={val}
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && dirty && onCommit(Number(val))}
            className={`w-20 px-2 py-1 rounded-lg border ${night.overridden ? "border-gold bg-gold/10" : "border-stone"} outline-none focus:border-gold`} />
          {dirty && (
            <button data-testid="timeline-save-btn" onClick={() => onCommit(Number(val))} className="text-green-600 hover:text-green-800"><Check size={16} /></button>
          )}
          {night.overridden && !dirty && (
            <span className="text-[10px] uppercase tracking-wide text-gold flex items-center gap-1"><Pencil size={10} /> manuale</span>
          )}
        </div>
      </td>
      <td className="py-2 px-2">
        <div className="flex flex-wrap gap-1.5">
          {night.occupancy.map((o) => (
            <span key={o.guests} data-testid={`occupancy-pill-${o.guests}`}
              title={`Totale ${o.guests} ${o.guests === 1 ? "persona" : "persone"}: €${o.price}`}
              className="text-xs bg-stone/70 rounded-full px-2 py-0.5 text-fir-deep/80">
              {o.guests}p €{o.price}
              {o.guests > 1 && <span className="text-fir-deep/50 ml-1">({o.pct}% · +€{o.per_person})</span>}
            </span>
          ))}
        </div>
      </td>
      <td className="py-2 px-2 text-right">
        {night.overridden && (
          <button data-testid="timeline-reset-btn" onClick={onReset} className="text-fir-deep/50 hover:text-red-600" title="Ripristina prezzo IA"><RotateCcw size={15} /></button>
        )}
      </td>
    </tr>
  );
}

function NumRow({ label, value, onChange, testid }) {
  return (
    <div className="mb-3">
      <label className="text-xs uppercase tracking-wide text-fir-deep/60">{label}</label>
      <input data-testid={testid} type="number" value={value} onChange={(e) => onChange(Number(e.target.value))} className="input-alpine w-full mt-1" />
    </div>
  );
}
